import type { PermissionAction, PermissionSubject } from "@milqit/shared-types";
import { ADMIN_ROLE_LABELS, PERMISSION_SUBJECT_LABELS } from "@milqit/shared-types";
import { ArrowLeft, ShieldX } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";

import { buttonVariants } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { useAbility } from "@/lib/auth/ability";
import type { AdminSession } from "@/lib/auth/auth-store";
import { navItemFor } from "@/lib/navigation";

/** Pages that change data rather than show it, by path. */
const WRITE_PAGES: { pattern: RegExp; action: PermissionAction; subject: PermissionSubject }[] = [
  { pattern: /^\/products\/new$/, action: "create", subject: "Product" },
  { pattern: /^\/products\/[^/]+\/edit$/, action: "update", subject: "Product" },
];

function requirementFor(pathname: string) {
  const write = WRITE_PAGES.find((page) => page.pattern.test(pathname));
  if (write) return write;
  const item = navItemFor(pathname);
  return item ? { action: "read" as const, subject: item.subject } : null;
}

/** Shows the page only when the admin's role may open it; otherwise explains why not. */
export function RequireAccess({
  role,
  children,
}: {
  role: AdminSession["role"];
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const ability = useAbility();
  const required = requirementFor(pathname);
  if (!required || ability.can(required.action, required.subject)) return children;

  return (
    <Empty className="border py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <ShieldX />
        </EmptyMedia>
        <EmptyTitle>You don&apos;t have access to this page</EmptyTitle>
        <EmptyDescription>
          {ADMIN_ROLE_LABELS[role]} accounts can&apos;t{" "}
          {required.action === "read" ? "view" : required.action}{" "}
          {PERMISSION_SUBJECT_LABELS[required.subject]}. Ask an owner if you need it.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Link to="/" className={buttonVariants({ variant: "outline" })}>
          <ArrowLeft /> Back to the dashboard
        </Link>
      </EmptyContent>
    </Empty>
  );
}
